import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, siteConfigService } from '../services/api';
import { useSiteConfig } from '../context/SiteConfigContext';
import type { SiteConfigData, SiteNavItem, SavedTheme, SiteConfigShortcut } from '../types';
import {
    COLOR_PALETTE,
    DEFAULT_ORIGINAL_CONFIG,
    THEME_FIELDS,
    defaultFeatures,
    defaultHomeShortcuts,
} from '../admin/adminSiteConfigData';
import {
    DEFAULT_GUILDRANK_CONFIG,
    mergeGuildRankConfig,
    type GuildRankElementKey,
} from '../utils/guildRankConfig';
import type { GuildRankEffectKey } from '../types';

function mergeHomeShortcuts(configItems?: SiteConfigShortcut[]): SiteConfigShortcut[] {
    const merged = new Map<string, SiteConfigShortcut>();
    for (const item of defaultHomeShortcuts) merged.set(item.id, { ...item });
    for (const item of configItems ?? []) {
        if (item?.id) merged.set(item.id, { ...merged.get(item.id), ...item });
    }
    return defaultHomeShortcuts.map(d => merged.get(d.id) ?? { ...d });
}

export function useAdminSiteConfig() {
    const navigate = useNavigate();
    const { refresh } = useSiteConfig();
    const [config, setConfig] = useState<SiteConfigData>({});
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [selectedThemeKey, setSelectedThemeKey] = useState<string | null>(null);

    useEffect(() => {
        const verifyAndLoad = async () => {
            if (!authService.isAuthenticated()) {
                navigate('/login');
                return;
            }
            if (!(await authService.checkIsAdmin())) {
                navigate('/');
                return;
            }
            try {
                const data = await siteConfigService.getConfig();
                const saved = data.savedThemes && Array.isArray(data.savedThemes) ? data.savedThemes : [];
                const savedThemes: SavedTheme[] = [];
                for (let i = 0; i < 5; i++) {
                    savedThemes.push(saved[i] ? { name: saved[i].name ?? `Tema ${i + 1}`, theme: { ...saved[i].theme } } : { name: `Tema ${i + 1}`, theme: {} });
                }
                setConfig({
                    links: { ...data.links },
                    home: {
                        ...data.home,
                        features: data.home?.features?.length ? [...data.home.features] : defaultFeatures,
                        shortcuts: mergeHomeShortcuts(data.home?.shortcuts),
                    },
                    theme: { ...data.theme },
                    general: { ...data.general },
                    download: { ...data.download },
                    savedThemes,
                    navbar: {
                        ...data.navbar,
                        logo: { ...data.navbar?.logo },
                        menuItems: data.navbar?.menuItems?.length ? [...data.navbar.menuItems] : [...(DEFAULT_ORIGINAL_CONFIG.navbar?.menuItems || [])],
                    },
                    guildRank: mergeGuildRankConfig(data.guildRank),
                });
            } catch {
                const savedThemes: SavedTheme[] = Array.from({ length: 5 }, (_, i) => ({ name: `Tema ${i + 1}`, theme: {} }));
                setConfig({ links: {}, home: { features: defaultFeatures, shortcuts: defaultHomeShortcuts.map(s => ({ ...s })) }, theme: {}, general: {}, savedThemes, navbar: { ...DEFAULT_ORIGINAL_CONFIG.navbar } });
            }
        };
        verifyAndLoad();
    }, [navigate]);

    const updateLinks = (key: 'discord' | 'supportTicket', value: string) => {
        setConfig(prev => ({
            ...prev,
            links: { ...prev.links, [key]: value }
        }));
    };

    const updateHome = (key: string, value: string) => {
        setConfig(prev => ({
            ...prev,
            home: { ...prev.home, [key]: value }
        }));
    };

    const updateFeature = (index: number, field: 'title' | 'description' | 'image' | 'icon', value: string) => {
        setConfig(prev => {
            const features = [...(prev.home?.features || defaultFeatures)];
            if (!features[index]) features[index] = { title: '', description: '' };
            features[index] = { ...features[index], [field]: value };
            return { ...prev, home: { ...prev.home, features } };
        });
    };

    const updateShortcut = (index: number, field: 'imageUrl' | 'to' | 'icon', value: string) => {
        setConfig(prev => {
            const shortcuts = [...(prev.home?.shortcuts || defaultHomeShortcuts)];
            if (!shortcuts[index]) shortcuts[index] = { ...defaultHomeShortcuts[index] };
            shortcuts[index] = { ...shortcuts[index], [field]: value };
            return { ...prev, home: { ...prev.home, shortcuts } };
        });
    };

    const updateTheme = (key: string, value: string) => {
        setConfig(prev => ({
            ...prev,
            theme: { ...prev.theme, [key]: value }
        }));
    };

    const updateGeneral = (key: string, value: string) => {
        setConfig(prev => ({
            ...prev,
            general: { ...prev.general, [key]: value }
        }));
    };

    const updateDownload = (key: string, lang: 'en' | 'pt-BR', value: string) => {
        setConfig(prev => ({
            ...prev,
            download: {
                ...prev.download,
                [key]: {
                    ...(prev.download as Record<string, { en?: string; 'pt-BR'?: string }>)?.[key],
                    [lang]: value,
                },
            },
        }));
    };

    const updateLogo = (key: 'src' | 'alt', value: string) => {
        setConfig(prev => ({
            ...prev,
            navbar: {
                ...prev.navbar,
                logo: { ...prev.navbar?.logo, [key]: value }
            }
        }));
    };

    const updateLogoNumber = (key: 'heightPx', value: number) => {
        setConfig(prev => ({
            ...prev,
            navbar: {
                ...prev.navbar,
                logo: { ...prev.navbar?.logo, [key]: value }
            }
        }));
    };

    const updateNavbarPosition = (pos: 'left' | 'center') => {
        setConfig(prev => ({
            ...prev,
            navbar: { ...prev.navbar, logoPosition: pos }
        }));
    };

    const updateMenuItem = (id: string, patch: Partial<SiteNavItem>) => {
        setConfig(prev => {
            const items = [...(prev.navbar?.menuItems || [])];
            const idx = items.findIndex(i => i.id === id);
            if (idx === -1) return prev;
            items[idx] = { ...items[idx], ...patch };
            return { ...prev, navbar: { ...prev.navbar, menuItems: items } };
        });
    };

    const addMenuItem = () => {
        setConfig(prev => {
            const items = [...(prev.navbar?.menuItems || [])];
            const nextOrder = (items.reduce((max, i) => Math.max(max, i.order ?? 0), 0) || 0) + 1;
            const newItem: SiteNavItem = {
                id: `custom-${Date.now()}`,
                type: 'external',
                href: 'https://example.com',
                label: { en: 'New Link', 'pt-BR': 'Novo Link' },
                visible: true,
                newTab: true,
                order: nextOrder,
            };
            return { ...prev, navbar: { ...prev.navbar, menuItems: [...items, newItem] } };
        });
    };

    const removeMenuItem = (id: string) => {
        setConfig(prev => {
            const items = (prev.navbar?.menuItems || []).filter(i => i.id !== id);
            return { ...prev, navbar: { ...prev.navbar, menuItems: items } };
        });
    };

    const savedThemes = config.savedThemes ?? Array.from({ length: 5 }, (_, i) => ({ name: `Tema ${i + 1}`, theme: {} as Record<string, string> }));
    const guildRank = mergeGuildRankConfig(config.guildRank);

    const updateGuildRankTopLevel = (key: 'cardMaxWidth' | 'highlightFirst', value: number | boolean) => {
        setConfig(prev => ({
            ...prev,
            guildRank: { ...mergeGuildRankConfig(prev.guildRank), [key]: value },
        }));
    };

    const updateGuildRankElement = (key: GuildRankElementKey, patch: Record<string, number | string | boolean>) => {
        setConfig(prev => {
            const base = mergeGuildRankConfig(prev.guildRank);
            return {
                ...prev,
                guildRank: {
                    ...base,
                    [key]: { ...base[key], ...patch },
                },
            };
        });
    };

    const updateGuildRankEffects = (key: GuildRankEffectKey, value: boolean) => {
        setConfig(prev => {
            const base = mergeGuildRankConfig(prev.guildRank);
            return { ...prev, guildRank: { ...base, effects: { ...base.effects, [key]: value } } };
        });
    };

    const handleRestoreGuildRank = () => {
        if (!window.confirm('Restaurar o layout dos cards de guild ao padrão?')) return;
        setConfig(prev => ({
            ...prev,
            guildRank: { ...DEFAULT_GUILDRANK_CONFIG },
        }));
        setMessage('Layout dos cards de guild restaurado ao padrão. Clique em "Salvar" para aplicar.');
    };

    const emptyTheme: Record<string, string> = {};
    const defaultSavedThemesList: SavedTheme[] = Array.from({ length: 5 }, (_, i) => ({
        name: 'Tema ' + (i + 1),
        theme: emptyTheme,
    }));

    const setSavedThemeSlot = (index: number, patch: { name?: string; theme?: Record<string, string> }) => {
        setConfig(prev => {
            const base = prev.savedThemes && prev.savedThemes.length >= 5 ? prev.savedThemes : defaultSavedThemesList;
            const list = base.slice();
            if (!list[index]) list[index] = { name: 'Tema ' + (index + 1), theme: emptyTheme };
            list[index] = {
                name: patch.name !== undefined ? patch.name : list[index].name,
                theme: patch.theme !== undefined ? { ...patch.theme } : { ...list[index].theme },
            };
            return { ...prev, savedThemes: list };
        });
    };

    const saveCurrentThemeToSlot = (index: number) => {
        const currentTheme = { ...(config.theme || {}) };
        const name = savedThemes[index]?.name || `Tema ${index + 1}`;
        setSavedThemeSlot(index, { name, theme: currentTheme });
        setMessage(`Tema atual salvo no slot ${index + 1}. Clique em "Salvar" para gravar.`);
    };

    const applySavedTheme = async (index: number) => {
        const slot = savedThemes[index];
        if (!slot?.theme || Object.keys(slot.theme).length === 0) {
            setMessage('Este slot está vazio. Salve o tema atual nele primeiro.');
            return;
        }
        const newConfig = { ...config, theme: { ...slot.theme } };
        setConfig(newConfig);
        setSaving(true);
        setMessage(null);
        try {
            await siteConfigService.updateConfig(newConfig);
            await refresh();
            setMessage(`Tema do slot ${index + 1} aplicado e salvo.`);
        } catch (err: any) {
            setMessage(err.response?.data?.message || 'Erro ao aplicar tema.');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        try {
            await siteConfigService.updateConfig(config);
            await refresh();
            setMessage('Configurações salvas com sucesso.');
        } catch (err: any) {
            setMessage(err.response?.data?.message || 'Erro ao salvar.');
        } finally {
            setSaving(false);
        }
    };

    const handleRestoreTheme = () => {
        if (!window.confirm('Restaurar apenas o tema (cores e efeitos) ao original?')) return;
        setConfig(prev => ({
            ...prev,
            theme: { ...DEFAULT_ORIGINAL_CONFIG.theme },
        }));
        setMessage('Tema restaurado ao original. Clique em "Salvar" para aplicar no site.');
    };

    const handleRestoreAll = () => {
        if (!window.confirm('Restaurar todas as configurações ao original? Os 5 temas salvos não serão alterados.')) return;
        setConfig(prev => ({
            links: { ...DEFAULT_ORIGINAL_CONFIG.links },
            home: {
                ...DEFAULT_ORIGINAL_CONFIG.home,
                features: DEFAULT_ORIGINAL_CONFIG.home?.features ? [...DEFAULT_ORIGINAL_CONFIG.home.features] : defaultFeatures,
                shortcuts: defaultHomeShortcuts.map(s => ({ ...s })),
            },
            theme: { ...DEFAULT_ORIGINAL_CONFIG.theme },
            general: { ...DEFAULT_ORIGINAL_CONFIG.general },
            navbar: { ...DEFAULT_ORIGINAL_CONFIG.navbar, menuItems: [...(DEFAULT_ORIGINAL_CONFIG.navbar?.menuItems || [])] },
            savedThemes: prev.savedThemes?.length ? [...prev.savedThemes] : Array.from({ length: 5 }, (_, i) => ({ name: `Tema ${i + 1}`, theme: {} })),
        }));
        setMessage('Configurações restauradas ao original. Clique em "Salvar" para aplicar no site.');
    };

    const features = config.home?.features || defaultFeatures;
    const shortcuts = config.home?.shortcuts || defaultHomeShortcuts;

    return {
        config,
        saving,
        message,
        selectedThemeKey,
        setSelectedThemeKey,
        features,
        shortcuts,
        savedThemes,
        guildRank,
        updateGuildRankTopLevel,
        updateGuildRankElement,
        updateGuildRankEffects,
        handleRestoreGuildRank,
        THEME_FIELDS,
        COLOR_PALETTE,
        updateLinks,
        updateHome,
        updateFeature,
        updateShortcut,
        updateTheme,
        updateGeneral,
        updateDownload,
        updateLogo,
        updateLogoNumber,
        updateNavbarPosition,
        updateMenuItem,
        addMenuItem,
        removeMenuItem,
        setSavedThemeSlot,
        saveCurrentThemeToSlot,
        applySavedTheme,
        handleSave,
        handleRestoreTheme,
        handleRestoreAll,
    };
}
